/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "actions";
import * as ActionTypes from "constants/ActionTypes";
import { useHistory } from "react-router-dom";
import {
    Box,
    Avatar,
    Typography,
    IconButton,
    Rating,
    Chip,
    useMediaQuery,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlaceIcon from "@mui/icons-material/Place";
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EventIcon from '@mui/icons-material/Event';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StoriesPlayer from "components/StoriesPlayer/StoriesPlayer";
import {
    getLogo,
    openGoogleMapLocation,
    openInstagram,
    openWhatsApp,
    getURI,
    isFeatureSupported,
    isAuthorized
} from "helpers/utils";
import CircularProgress from "@mui/material/CircularProgress";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import {FlexBox} from "components/flex-box";
import ButtonComponent from "components/Button";
import { AppointmentBooking } from "components/appointments";

const FlutterStyleShopHeader = (props) => {
    const {
        shopinfo,
        onCancel,
        shopId, isLoading,
        userMainAddStoreToFavlist,
        checkPendingPayments,
        pendingPayments
    } = props;

    const theme = useTheme();
    const history = useHistory();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
    const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px
    const [shopModeState, setShopModeState] = useState("PREVIEW");
    const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);

    // Check for pending payments when shop info is loaded
    useEffect(() => {
        if (isAuthorized() && shopinfo && shopinfo.subdomain && !pendingPayments.isLoading && pendingPayments.count === 0) {
            checkPendingPayments(shopinfo.subdomain, false);
        }
    }, [shopinfo, shopinfo?.subdomain]);

    const handleBack = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const openGoogleMap = () => {
        if (shopinfo.location) {
            openGoogleMapLocation(shopinfo.location);
        }
    };

    const handleBookAppointment = () => {
        setAppointmentDialogOpen(true);
    };

    const handleAppointmentSuccess = (appointment) => {
        setAppointmentDialogOpen(false);
        // Navigate to user appointments page
        history.push(getURI(process.env.REACT_APP_TYPE, "appointment_success", "", "", ""));
    };

    const handleAppointmentError = (error) => {
        // Close the dialog when there's a booking error
        setAppointmentDialogOpen(false);
    };

    const handleViewOrders = () => {
        history.push(getURI(process.env.REACT_APP_TYPE, "orders", shopinfo.subdomain, "", ""));
    };

    const addShopFavourite = () => {
        userMainAddStoreToFavlist(shopinfo.id);
    };

    // Responsive sizing values matching ShopHeaderMiniOverlay
    const avatarSize = isMobile ? 60 : isTablet ? 100 : 120;
    const avatarWidth = isMobile ? "30%" : "25%";
    const contentWidth = isMobile ? "70%" : "75%";
    const iconSize = isMobile ? "medium" : "large";
    const buttonSize = isMobile ? "small" : "medium";
    const titleVariant = isMobile ? "h6" : isTablet ? "h5" : "h5";
    const noteVariant = isMobile ? "h6" : isTablet ? "h5" : "h5";
    const areaVariant = isMobile ? "body2" : "h6";
    const padding = isMobile ? "2px 2px" : "4px 4px";
    const borderWidth = isMobile ? '2px' : '3px';
    const iconPadding = isMobile ? 2 : 3;
    const buttonMargin = isMobile ? 2 : 3;

    const renderDefaultModule = () => (
        <>
        <Box sx={{ position: 'relative' }}>
            {/* Back Button */}
            {process.env.REACT_APP_TYPE !== "USERSITE" &&
                <IconButton
                    onClick={handleBack}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        zIndex: 10,
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.5)',
                        }
                    }}
                    size={iconSize}
                >
                    <ArrowBackIcon fontSize={iconSize}/>
                </IconButton>
            }
            <Box
                sx={{
                    display: "flex",
                    p: padding,
                    height: "100%",
                    alignItems: "center",
                    bgcolor: "#E53E3E",
                    flexWrap: { xs: 'nowrap', sm: 'nowrap' }
                }}
            >
                {isLoading ? 
                    <Box sx={{
                        height: avatarSize,
                        m: 2,
                        display: 'flex',
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%"
                    }}>
                        <CircularProgress sx={{ color: '#fff' }} size={isMobile ? 30 : 40} />
                    </Box>
                    : 
                    <>
                        <Box onClick={handlePlayShopStory} width={avatarWidth} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box sx={{ position: 'relative'}} width={avatarSize}>
                            <Avatar
                                src={getLogo(shopinfo.logo)}
                                sx={{
                                    height: avatarSize,
                                    width: avatarSize,
                                    mb: { xs: 1, sm: 2 },
                                    border: `${borderWidth} solid white`,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                            />
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    top: '80%',
                                    left: '80%',
                                    transform: 'translate(-80%, -80%)',
                                    backgroundColor: 'white',
                                    color: 'primary.main',
                                    width: 16,
                                    height: 16,
                                    zIndex: 1,
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    },
                                }}
                            >
                                <PlayCircleIcon fontSize="medium" />
                            </IconButton>
                            </Box>
                        </Box>
                        <Box width={contentWidth} sx={{ pl: { xs: 0.5, sm: 1 } }}>
                            <Box width="100%" alignItems="left">
                                <Typography
                                    variant={titleVariant}
                                    sx={{
                                        fontWeight: 'bold',
                                        textAlign: 'left',
                                        mb: { xs: 0.5, sm: 1 },
                                        mt: { xs: 1, sm: 1.5 },
                                        color: 'white',
                                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.0rem' }
                                    }}
                                >
                                    {shopinfo.title}
                                </Typography>
                                {shopinfo.area && (
                                    <Typography
                                        variant={areaVariant}
                                        sx={{
                                            color: 'rgba(255,255,255,0.8)',
                                            textAlign: 'left',
                                            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                                        }}
                                    >
                                        {shopinfo.area}
                                    </Typography>
                                )}
                            </Box>
                            <FlexBox width="100%" alignItems="center" sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                                <FlexBox width="40%" sx={{ justifyContent: 'flex-start' }}>
                                    <IconButton 
                                        sx={{ p: iconPadding, paddingLeft: 0, color: 'white' }}
                                        onClick={() => openGoogleMap()}
                                        size={iconSize}
                                    >
                                        <PlaceIcon fontSize={iconSize} />
                                    </IconButton>
                                    {shopinfo.instahandle &&
                                        <IconButton 
                                            sx={{ p: iconPadding, paddingLeft: 0, color: 'white' }}
                                            onClick={() => openInstagram(shopinfo.instahandle)}
                                            size={iconSize}
                                        >
                                            <InstagramIcon fontSize={iconSize} />
                                        </IconButton>
                                    }
                                    {shopinfo.whatsapphandle &&
                                        <IconButton 
                                            sx={{ p: iconPadding, paddingLeft: 0, color: 'white' }}
                                            onClick={() => openWhatsApp(shopinfo.whatsapphandle)}
                                            size={iconSize}
                                        >
                                            <WhatsAppIcon fontSize={iconSize} />
                                        </IconButton>
                                    }

                                </FlexBox>
                                <FlexBox width="60%" sx={{ justifyContent: 'flex-start', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
                                    {/* Follow Button */}
                                    <Box width={{ xs: '48%', sm: '40%' }} alignItems="left">
                                        {parseInt(shopinfo.fav) > 0 ?
                                            <Box
                                                sx={{
                                                    mt: { xs: 0.5, sm: buttonMargin },
                                                    mb: { xs: 0.5, sm: buttonMargin },
                                                    p: { xs: 1, sm: 2 },
                                                    border: `${borderWidth} solid primary`,
                                                    minWidth: 'auto',
                                                    color: 'white',
                                                    backgroundColor: 'primary',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    textAlign: 'center'
                                                }}>
                                                <b>{isMobile ? 'Following' : 'Following'}</b>
                                            </Box>
                                            :
                                            isLoading ? <CircularProgress size={isMobile ? "0.8rem" : "1rem"} sx={{ color: 'white' }}/> : <>
                                                <ButtonComponent
                                                    sx={{
                                                        mt: { xs: 0.5, sm: buttonMargin },
                                                        mb: { xs: 0.5, sm: buttonMargin },
                                                        border: `${borderWidth} solid white`,
                                                        minWidth: 'auto',
                                                        color: 'white',
                                                        backgroundColor: 'primary',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                                        px: { xs: 1, sm: 2 },
                                                        py: { xs: 0.5, sm: 1 }
                                                    }}
                                                    type="primary"
                                                    variant="contained"
                                                    name={isMobile ? "Follow" : "Follow us!"}
                                                    size={buttonSize}
                                                    onClick={() => addShopFavourite()}
                                                />
                                            </>
                                        }
                                    </Box>
                                    {/* Appointment Button */}
                                    {shopinfo.appointment_enabled && (
                                        <Box width={{ xs: '48%', sm: '40%' }} alignItems="left">
                                            <ButtonComponent
                                                sx={{
                                                    mt: { xs: 0.5, sm: buttonMargin },
                                                    mb: { xs: 0.5, sm: buttonMargin },
                                                    border: `${borderWidth} solid white`,
                                                    color: 'white',
                                                    minWidth: 'auto',
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                                                    px: { xs: 1, sm: 2 },
                                                    py: { xs: 0.5, sm: 1 }
                                                }}
                                                type="primary"
                                                variant="contained"
                                                name={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <EventIcon fontSize="small" />
                                                        <span>{isMobile ? "Book" : "Book Appointment"}</span>
                                                    </Box>
                                                }
                                                size={buttonSize}
                                                onClick={handleBookAppointment}
                                            />
                                        </Box>
                                        )}
                                </FlexBox>
                            </FlexBox>
                        </Box>
                    </>
                }
            </Box>
        </Box>
        </>
    );

    const handlePlayShopStory = () => {
        setShopModeState("STORIES");
    }

    const handleResetShopStory = () => {
        setShopModeState("PREVIEW");
    }

    const renderShopAsStories = (shopData) => (

        <div>
            <StoriesPlayer
                storiesData={shopData}
                setAsFav={addShopFavourite}
                onCancel={() => handleResetShopStory()}
            />
        </div>
    );

    const renderShopMode = (shopMode) => {
        switch (shopMode) {

            case "STORIES":
                const {id, shop_id, title, logo, video, topproducts, area} = shopinfo;
                let shopStoryData = [];
                let stories = [];

                if (video && video.length > 0) {
                    stories.push({
                        video: video[0].cdn.secure_url,
                        videoimage: logo && logo[0].cdn.secure_url
                    });
                } else {
                    topproducts && topproducts.map((product) => (
                        stories.push({
                            image: product.cdn.secure_url,
                        })
                    ));
                }

                if (stories.length < 1) {
                    stories.push({
                        image: logo && logo[0].cdn.secure_url,
                    });
                }

                shopStoryData.push(
                    {
                        user: {
                            avatar: logo[0].cdn.secure_url,
                            name: title,
                            area: area,
                        },
                        item: shopinfo,
                        favId: shop_id,
                        stories: stories,
                    }
                );

                return renderShopAsStories(shopStoryData);

            case "PREVIEW":
            default:
                // causes render again on closing stories - reload of objects inside
                return renderDefaultModule();
        }
    }

    return (
        <Box>
            { renderShopMode(shopModeState)}
            {/* Pending Payment Orders Button */}
            {pendingPayments.has_pending_payments && (
                <Box sx={{ bgcolor: "#daf0ff",}} onClick={handleViewOrders}>
                    <Typography
                        variant={titleVariant}
                        sx={{
                            textAlign: 'center',
                            color: '#000',
                            fontSize: { xs: '1.0rem', sm: '1.25rem', md: '1.5rem' }
                        }}
                    >
                        {isMobile ? "Payment pending" : "Order Accepted - Payment pending"} <Typography  sx={{textDecoration: "underline"}}>Click here to make payment</Typography>
                    </Typography>
                </Box>
            )}
        
        {/* Appointment Booking Dialog */}
        <Dialog 
            open={appointmentDialogOpen} 
            onClose={() => setAppointmentDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Appointment for Video Shopping
            </DialogTitle>
            <DialogContent>
                {shopinfo && (
                    <AppointmentBooking
                        storeId={shopinfo.id || shopinfo._id}
                        storeName={shopinfo.title}
                        onBookingSuccess={handleAppointmentSuccess}
                        onBookingError={handleAppointmentError}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAppointmentDialogOpen(false)}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </Box>
    );

};

FlutterStyleShopHeader.propTypes = {
    shopinfo: PropTypes.object.isRequired,
    onCancel: PropTypes.func,
    shopId: PropTypes.string.isRequired,
};

const _FlutterStyleShopHeader = connect(
    ({ bootupInfo, userorders }) => ({
        bootupInfo,
        shopinfo: bootupInfo.shopinfo,
        isLoading: bootupInfo.isLoading,
        pendingPayments: userorders.pendingPayments,
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch),
)(FlutterStyleShopHeader);

export { _FlutterStyleShopHeader as default };



